/**
 * Adapted from PlayCanvas Engine's gsplat-frustum-culler.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type {
  GSplatBufferLeaf,
  GSplatGraphicsAdapter,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatWorldBoundsGroup } from './world/GSplatWorldState'

export const GSPLAT_BOUNDS_ENTRY_WORDS = 8
export const GSPLAT_TRANSFORM_WORDS = 12

interface BoundsSphereWriter {
  writeBoundsSpheres(data: Float32Array, offset: number): void
}

/** WorkBuffer-owned bounds/transform storage；compute cull 只借用这些 leaf。 */
export class GSplatFrustumCuller {
  boundsBuffer: GSplatBufferLeaf | null = null
  transformsBuffer: GSplatBufferLeaf | null = null
  totalBoundsEntries = 0
  readonly frustumPlanes = new Float32Array(24)
  readonly fisheyeCameraPos = new Float32Array(3)
  readonly fisheyeCameraForward = new Float32Array(3)
  fisheyeMaxTheta = Math.PI

  private allocatedBoundsEntries = 0
  private allocatedTransformCount = 0
  private boundsData: ArrayBuffer | null = null
  private transformsData: Float32Array | null = null
  private readonly viewProjection = new THREE.Matrix4()
  private readonly frustum = new THREE.Frustum()
  private destroyed = false

  constructor(private readonly adapter: GSplatGraphicsAdapter) {}

  updateBoundsData(boundsGroups: readonly GSplatWorldBoundsGroup[]): void {
    this.assertActive()
    const totalEntries = sumBoundsEntries(boundsGroups)
    this.totalBoundsEntries = totalEntries
    if (totalEntries === 0) return

    let staged: GSplatBufferLeaf | null = null
    let target = this.boundsBuffer
    let backing = this.boundsData
    if (totalEntries > this.allocatedBoundsEntries) {
      staged = this.adapter.createBuffer({
        label: 'GSplatFrustumCuller.bounds',
        byteLength: totalEntries * GSPLAT_BOUNDS_ENTRY_WORDS * Uint32Array.BYTES_PER_ELEMENT,
        usages: ['storage', 'copy-dst'],
      })
      target = staged
      backing = new ArrayBuffer(staged.byteLength)
    }
    if (!target || !backing) throw new Error('GSplat frustum bounds storage 未初始化')

    const floatView = new Float32Array(backing)
    const uintView = new Uint32Array(backing)
    floatView.fill(0)
    const spheres = new Float32Array(totalEntries * 4)
    for (let groupIndex = 0; groupIndex < boundsGroups.length; groupIndex++) {
      const group = boundsGroups[groupIndex]
      writeGroupSpheres(group, spheres)
      for (let index = 0; index < group.numBoundsEntries; index++) {
        const source = (group.boundsBaseIndex + index) * 4
        const targetIndex = (group.boundsBaseIndex + index) * GSPLAT_BOUNDS_ENTRY_WORDS
        floatView[targetIndex] = spheres[source]
        floatView[targetIndex + 1] = spheres[source + 1]
        floatView[targetIndex + 2] = spheres[source + 2]
        floatView[targetIndex + 3] = spheres[source + 3]
        uintView[targetIndex + 4] = groupIndex
      }
    }

    try {
      this.adapter.updateBuffer(target, floatView)
    } catch (error) {
      staged?.dispose()
      throw error
    }
    if (staged) {
      const previous = this.boundsBuffer
      this.boundsBuffer = staged
      this.boundsData = backing
      this.allocatedBoundsEntries = totalEntries
      previous?.dispose()
    }
  }

  updateTransformsData(boundsGroups: readonly GSplatWorldBoundsGroup[]): void {
    this.assertActive()
    const count = boundsGroups.length
    if (count === 0) return

    let staged: GSplatBufferLeaf | null = null
    let target = this.transformsBuffer
    let data = this.transformsData
    if (count > this.allocatedTransformCount) {
      staged = this.adapter.createBuffer({
        label: 'GSplatFrustumCuller.transforms',
        byteLength: count * GSPLAT_TRANSFORM_WORDS * Float32Array.BYTES_PER_ELEMENT,
        usages: ['storage', 'copy-dst'],
      })
      target = staged
      data = new Float32Array(count * GSPLAT_TRANSFORM_WORDS)
    }
    if (!target || !data) throw new Error('GSplat frustum transform storage 未初始化')

    let offset = 0
    for (const group of boundsGroups) {
      group.splat.node.updateWorldMatrix(true, false)
      const matrix = group.splat.node.matrixWorld.elements
      data[offset++] = matrix[0]
      data[offset++] = matrix[4]
      data[offset++] = matrix[8]
      data[offset++] = matrix[12]
      data[offset++] = matrix[1]
      data[offset++] = matrix[5]
      data[offset++] = matrix[9]
      data[offset++] = matrix[13]
      data[offset++] = matrix[2]
      data[offset++] = matrix[6]
      data[offset++] = matrix[10]
      data[offset++] = matrix[14]
    }

    try {
      this.adapter.updateBuffer(target, data)
    } catch (error) {
      staged?.dispose()
      throw error
    }
    if (staged) {
      const previous = this.transformsBuffer
      this.transformsBuffer = staged
      this.transformsData = data
      this.allocatedTransformCount = count
      previous?.dispose()
    }
  }

  computeFrustumPlanes(camera: THREE.Camera): void {
    this.assertActive()
    camera.updateWorldMatrix(true, false)
    this.viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
    this.frustum.setFromProjectionMatrix(
      this.viewProjection,
      this.adapter.backend === 'webgpu' ? THREE.WebGPUCoordinateSystem : THREE.WebGLCoordinateSystem
    )
    this.setFrustumPlanes(this.frustum)
  }

  setFrustumPlanes(frustum: THREE.Frustum): void {
    this.assertActive()
    for (let index = 0; index < frustum.planes.length; index++) {
      const plane = frustum.planes[index]
      this.frustumPlanes[index * 4] = plane.normal.x
      this.frustumPlanes[index * 4 + 1] = plane.normal.y
      this.frustumPlanes[index * 4 + 2] = plane.normal.z
      this.frustumPlanes[index * 4 + 3] = plane.constant
    }
  }

  setFisheyeData(position: THREE.Vector3, forward: THREE.Vector3, maxTheta: number): void {
    this.assertActive()
    if (!Number.isFinite(maxTheta) || maxTheta < 0 || maxTheta > Math.PI) {
      throw new Error(`非法 GSplat fisheye maxTheta: ${maxTheta}`)
    }
    this.fisheyeCameraPos.set(position.toArray())
    this.fisheyeCameraForward.set(forward.clone().normalize().toArray())
    this.fisheyeMaxTheta = maxTheta
  }

  dispose(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.boundsBuffer?.dispose()
    this.transformsBuffer?.dispose()
    this.boundsBuffer = null
    this.transformsBuffer = null
    this.boundsData = null
    this.transformsData = null
    this.totalBoundsEntries = 0
    this.allocatedBoundsEntries = 0
    this.allocatedTransformCount = 0
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatFrustumCuller 已销毁')
  }
}

function sumBoundsEntries(groups: readonly GSplatWorldBoundsGroup[]): number {
  let total = 0
  for (const group of groups) {
    if (!Number.isSafeInteger(group.numBoundsEntries) || group.numBoundsEntries < 0) {
      throw new Error(`非法 GSplat bounds entry count: ${group.numBoundsEntries}`)
    }
    if (group.boundsBaseIndex !== total) {
      throw new Error(
        `GSplat boundsBaseIndex 不连续: actual=${group.boundsBaseIndex}, expected=${total}`
      )
    }
    total += group.numBoundsEntries
    if (!Number.isSafeInteger(total)) throw new Error('GSplat bounds entry count 超出安全整数')
  }
  return total
}

function writeGroupSpheres(group: GSplatWorldBoundsGroup, target: Float32Array): void {
  const writer = group.splat as typeof group.splat & Partial<BoundsSphereWriter>
  if (typeof writer.writeBoundsSpheres === 'function') {
    writer.writeBoundsSpheres(target, group.boundsBaseIndex * 4)
    return
  }
  if (group.numBoundsEntries !== 1) {
    throw new Error('GSplat multi-entry bounds 缺少 writeBoundsSpheres')
  }
  const center = group.splat.aabb.getCenter(new THREE.Vector3())
  const radius = group.splat.aabb.getSize(new THREE.Vector3()).multiplyScalar(0.5).length()
  const offset = group.boundsBaseIndex * 4
  target[offset] = center.x
  target[offset + 1] = center.y
  target[offset + 2] = center.z
  target[offset + 3] = radius
}
